import React, { useState, useRef, FC } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { Box, makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import * as yup from 'yup'
import { fetchMotorCarriersSearch } from 'apps/fleet-planner/utils/api'
import { Spinner, InlineMessage, INLINE_MESSAGE_VARIANTS, Button } from 'transfix-ui'
import { postMessageToMobileApp } from 'utils/mobileApp'
import { OnboardingCarrierList } from './CarrierSearchList'
import { useFindOnboardingMotorCarrierLazyQuery } from './graphql/FindOnboardingMotorCarrier'
import { ISearchedMotorCarrier } from './types'

const CarrierSearchSchema = yup.object({
  searchInput: yup.string().required('Use a valid DOT number to search for a carrier.'),
})

type CarrierSearchSchemaType = yup.InferType<typeof CarrierSearchSchema>

interface ICarrierSearchContainerProps {
  carriers: ISearchedMotorCarrier[]
  onAddCarrier: (carrier: ISearchedMotorCarrier) => void
  onRemoveCarrier: (index: number) => void
  onError?: (error?: string) => void
  /**
   * Query param set by mobile app webview
   * @default false
   */
  isMobileApp?: boolean
}

const useCarrierSearchContainerStyles = makeStyles(theme => ({
  inputGroup: {
    display: 'flex',
    flexWrap: 'wrap',

    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-end',
    },
  },
  input: {
    flex: '1 1 auto',
    paddingRight: theme.spacing(2),
  },
  button: {
    marginTop: 20,
    [theme.breakpoints.down('lg')]: {
      marginLeft: 'auto',
    },
    width: 200,
  },
}))

/**
 * Handles logic around searching for and adding a carrier to a user
 */
export const CarrierSearchContainer: FC<ICarrierSearchContainerProps> = ({
  carriers = [],
  onRemoveCarrier: onRemoveCarrierProp,
  onAddCarrier: onAddCarrierProp,
  onError,
  isMobileApp = false,
}) => {
  const isCarrierMutationEnabled = useFeatureFlag('fmsOnboardingCarrierMutation')
  const { isMobile } = useBreakpointContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const [loadingRest, setLoadingRest] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const { control, getValues, handleSubmit, reset } = useForm({
    defaultValues: {
      searchInput: '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(CarrierSearchSchema),
  })

  const [
    findOnboardingMotorCarrier,
    { loading: loadingGql },
  ] = useFindOnboardingMotorCarrierLazyQuery({
    onCompleted: ({ findOnboardingMotorCarrier: onboardingMotorCarrier }) => {
      if (onboardingMotorCarrier) {
        const {
          users: { nodes },
          ...rest
        } = onboardingMotorCarrier ?? {}
        const maskedAdminEmails = compact(nodes?.map(u => u?.dispatcher) ?? []).map(
          d => d.maskedEmail
        )
        onAddCarrierProp({
          ...rest,
          maskedAdminEmails,
        })
        reset({
          searchInput: '',
        })
      } else {
        postMessageToMobileApp(isMobileApp, 'CARRIER_NOT_FOUND')
        setErrorMessage(`Could not find carrier with DOT Number: ${getValues('searchInput')}.`)
      }
    },
  })

  const loading = loadingGql || loadingRest

  const addCarrierHandlerRest = async (dotNumber: number) => {
    try {
      setLoadingRest(true)
      const { motorCarrier } = await fetchMotorCarriersSearch({
        dotNumber,
      })
      if (!!motorCarrier && motorCarrier.legalName) {
        onAddCarrierProp({
          id: String(motorCarrier.id),
          dotNumber: motorCarrier.dotNumber,
          mcNumber: motorCarrier.mcNumber,
          legalName: motorCarrier.legalName,
          dbaName: motorCarrier.dbaName,
          maskedAdminEmails: motorCarrier.maskedAdminEmails,
        })
        reset({
          searchInput: '',
        })
      } else {
        postMessageToMobileApp(isMobileApp, 'CARRIER_NOT_FOUND')
        setErrorMessage(`Could not find carrier with DOT Number: ${dotNumber}.`)
      }
    } catch (e) {
      setErrorMessage('Could not find carrier.')
    } finally {
      setLoadingRest(false)
    }
  }
  /**
   * Search for a carrier via GET request, and add
   * result to local state
   */
  const addCarrierHandler = async (inputValue: string) => {
    const isAlreadyAdded = !!carriers.find(c => `${c.dotNumber}` === inputValue)
    const dotNumberVal = parseInt(inputValue, 10)
    const isValidNumber = typeof dotNumberVal === 'number'
    // If this carrier is already in the list, don't add it
    if (!inputValue || isAlreadyAdded || !isValidNumber) return

    setErrorMessage('')

    if (isCarrierMutationEnabled) {
      findOnboardingMotorCarrier({
        variables: {
          dotNumber: dotNumberVal,
        },
      })
    } else {
      addCarrierHandlerRest(dotNumberVal)
    }
    // Re-focus on input, in either outcome
    if (inputRef.current !== null) inputRef.current.focus()
    if (onError && !isEmpty(errorMessage)) {
      onError(errorMessage)
    }
  }

  const submitHandler = ({ searchInput = '' }: CarrierSearchSchemaType) => {
    if (loading) return
    addCarrierHandler(searchInput)
  }

  const classes = useCarrierSearchContainerStyles()

  return (
    <Box>
      <form onSubmit={handleSubmit(submitHandler)}>
        <Box mb={4}>
          <Box className={classes.inputGroup}>
            <Box className={classes.input}>
              <ControlledTextInput
                control={control}
                name='searchInput'
                defaultValue=''
                TextInputProps={{
                  dataTest: 'input-searchInput',
                  large: true,
                  autoFocus: true,
                  clearable: false,
                  placeholder: 'Enter your carrier’s DOT number',
                  label: 'DOT Number',
                  inputMode: 'numeric',
                  type: 'number',
                }}
              />
            </Box>
            <Box className={classes.button}>
              <Button
                color='secondary'
                type='submit'
                size='large'
                disabled={loading}
                fullWidth={isMobile}
                data-test='add-carrier-button'>
                Add Carrier
              </Button>
            </Box>
          </Box>
          {errorMessage && (
            <Box mt={2}>
              <InlineMessage
                message={errorMessage}
                type={INLINE_MESSAGE_VARIANTS.ALERT}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </form>
      <Box>
        {loading && !carriers.length && (
          <Box display='flex' alignItems='center' justifyContent='center' p={3} mb={3}>
            <Spinner position='relative' />
          </Box>
        )}
        <OnboardingCarrierList carriers={carriers} onRemoveCarrier={onRemoveCarrierProp} />
      </Box>
    </Box>
  )
}
